import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "./EditAccountDetails.scss";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { getPlan, planList, updatePlan } from "../../../services/PlanService";
import Loader from "../../../components/Loader";
import InputSearchField from "../../components/InputSearchField";
import { toast } from "sonner";
import CommonUtil from "../../../util/CommonUtils";

const EditAccountInfo = ({ mCode, yCode, planName, ...props }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [plan, setPlan] = useState({});
  const [monthlyPlans, setMonthlyPlans] = useState([]);
  const [yearlyPlans, setYearlyPlans] = useState([]);
  const [sequenceData, setSequenceData] = useState([]);

  const [errors, setErrors] = useState({
    name: "",
    planOrder: "",
    trialPeriod: "",
  });

  const validateBillingFields = () => {
    let isValid = true;

    if (CommonUtil.isEmptyString(monthlyPlans[0]?.name)) {
      setErrors({
        ...errors,
        name: t("RAL0797"),
      });
      isValid = false;
    }

    if (monthlyPlans[0]?.name?.length > 100) {
      setErrors({
        ...errors,
        name: t("RAL0808"),
      });
      isValid = false;
    }

    if (CommonUtil.isEmpty(monthlyPlans[0]?.planOrder+"")) {
      setErrors({
        ...errors,
        planOrder: t("RAL0799"),
      });
      isValid = false;
    }
    if (!CommonUtil.isEmpty(monthlyPlans[0]?.planOrder) && monthlyPlans[0]?.planOrder == 0) {
      setErrors({
        ...errors,
        planOrder: t("RAL0824"),
      });
      isValid = false;
    }

    if (!CommonUtil.isEmpty(monthlyPlans[0]?.planOrder) && monthlyPlans[0]?.planOrder > sequenceData[0]?.id + 1) {
      setErrors({
        ...errors,
        planOrder: t("RAL0812") + " " + (sequenceData[0]?.id + 1),
      });
      isValid = false;
    }

    if (CommonUtil.isEmptyString(monthlyPlans[0]?.trailPeriod)) {
      setErrors({
        ...errors,
        trialPeriod: t("RAL0798"),
      });
      isValid = false;
    } else if (monthlyPlans[0]?.trailPeriod > 60) {
      setErrors({
        ...errors,
        trialPeriod: t("RAL0800"),
      });
      isValid = false;
    }

    return isValid;
  };

  useEffect(() => {
    loadPlans();
  }, [planName]);

  useEffect(() => {
    loadPlansList();
  }, []);

  const loadPlansList = () => {
    setIsLoading(true);
    const intervalUnit = "MONTHS";
    var requestData = {
      intervalUnit,
    };
    planList(requestData).then((res) => {
      if (res?.status === 200 && res?.data?.code === "RCI0000") {
        let plan = [];
        res.data.data.plans
          .sort((o1, o2) => o2.planOrder - o1.planOrder)
          .forEach((extP) => {
            plan.push({
              id: extP.planOrder,
              name: extP.name,
            });
          });
        setSequenceData(plan);
        monthlyPlans({ ...monthlyPlans, plan_order: plan[0]?.id + 1 });
      }
      setIsLoading(false);
    });
  };

  const loadPlans = () => {
    setIsLoading(true);
    getPlan(planName)
      .then((res) => {
        if (res?.data?.code === "RCI0000") {
          const plansDetails = res.data.data.plansDetails;
          setPlan(plansDetails[0]);
          const monthly = plansDetails.filter(
            (plan) => plan.intervalUnit === "months"
          );
          const yearly = plansDetails.filter(
            (plan) => plan.intervalUnit === "years"
          );
          setMonthlyPlans(monthly);
          setYearlyPlans(yearly);
        }
      })
      .catch((error) => {
        console.error("Error loading plans:", error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleSave = () => {
    if (!validateBillingFields()) {
      return;
    }

    setIsLoading(true);

    const payload = {
      plan_name: monthlyPlans[0].name,
      plan_order: monthlyPlans[0].planOrder,
      trail_period: monthlyPlans[0].trailPeriod,
      update_plan: [
        ...monthlyPlans.map((plan) => ({
          description: plan.description,
          interval_unit: plan.intervalUnit,
          interval: plan.interval,
          price: plan.price,
        })),
        ...yearlyPlans.map((plan) => ({
          description: plan.description,
          interval_unit: plan.intervalUnit,
          interval: plan.interval,
          price: plan.price,
        })),
      ],
    };

    const monthlyCode = monthlyPlans[0]?.planCode;
    const yearlyCode = yearlyPlans[0]?.planCode;

    if (monthlyCode && yearlyCode) {
      updatePlan(monthlyCode, yearlyCode, payload)
        .then((res) => {
          if (res?.data?.code === "RCI0045") {
            toast.success(res.data.message);
            props.onClose(false);
            props.onUpdate(monthlyPlans[0]);
            localStorage.setItem("plan_name", plan.name);
            window.dispatchEvent(new Event('storage'));
          } else if (res.data.code === "RCE0250") {
            setErrors((prevErrors) => ({
              ...prevErrors,
              planOrder: t("RAL0818"),
            }));
          } else {
            // toast.error(res.data.message);
            setErrors((prevErrors) => ({
              ...prevErrors,
              name: res.data.message,
            }));
          }
        })
        .catch((error) => {
          console.error("Error updating plans:", error);
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      console.error("Monthly or yearly plan code is missing");
      setIsLoading(false);
    }
  };

  const handlePlanNameChange = (name) => {
    const updatedPlans = [...monthlyPlans];
    updatedPlans[0].name = name;
    setMonthlyPlans(updatedPlans);
    setErrors((prevErrors) => ({
      ...prevErrors,
      name: "",
    }));

  };

  const handleSequenceIdChange = (planOrder) => {
    if (!/^\d*$/.test(planOrder)) return;
    if (planOrder.length > 3) return;

    const updatedPlans = [...monthlyPlans];
    updatedPlans[0].planOrder = planOrder;
    setMonthlyPlans(updatedPlans);
    setErrors({
      ...errors,
      planOrder: "",
    });

    // if (!planOrder) {
    //   setErrors({
    //     ...errors,
    //     planOrder: t("RAL0799"),
    //   });
    // } else if (planOrder == 0) {
    //   setErrors({
    //     ...errors,
    //     planOrder: t("RAL0824"),
    //   });
    //   return false;
    // } else if (planOrder > sequenceData[0]?.id + 1) {
    //   setErrors({
    //     ...errors,
    //     planOrder: t("RAL0812") + " " + (sequenceData[0]?.id + 1),
    //   });
    //   return false;
    // } else {
    //   setErrors({
    //     ...errors,
    //     planOrder: "",
    //   });
    // }
  };

  const handleFreeTrialChange = (trailPeriod) => {
    if (!/^\d*$/.test(trailPeriod)) return;
    if (trailPeriod.length > 2) return;

    const updatedPlans = [...monthlyPlans];
    updatedPlans[0].trailPeriod = trailPeriod;
    setMonthlyPlans(updatedPlans);

    if (!trailPeriod) {
      setErrors({
        ...errors,
        trialPeriod: t("RAL0798"),
      });
    } else if (trailPeriod > 60) {
      setErrors({
        ...errors,
        trialPeriod: t("RAL0798"),
      });
    } else {
      setErrors({
        ...errors,
        trialPeriod: "",
      });
    }
  };

  const handleClose = () => {
    props.onClose(false);
  };

  return (
    <Dialog
      className="MuiDialog-Accountpaper"
      open={props.open}
      sx={{
        "& .MuiDialog-paper": {
          width: "80% !important",
          maxWidth: "65% !important",
          background: "#FFFFFF"
        },
      }}
    >
      <DialogTitle className="dailogbox-Accountcontainer">
        {/* {t("RAL0819")} */}
        Edit
        <p
          style={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            mxWidth: "300px",
            margin: "0 7px",
            font: "inherit",
            textAlign: "center"
          }}
        >
          {localStorage.getItem("plan_name")}
        </p>
        account
      </DialogTitle>

      <Divider className="dailogbox-Accountdivider" />

      <DialogContent className="dailogbox-Accountcontent">
        {isLoading && <Loader open={isLoading} />}

        {monthlyPlans.map((plan, index) => (
          <Box key={plan.id} className="dialogbox-input">
            <Grid item xs={6} mt={2}>
              <div className="editAccountlabel">Plan name</div>
              <InputSearchField
                label={t("RAL0820")}
                value={plan.name || ""}
                onChange={handlePlanNameChange}
                error={errors.name}
                helperText={errors.name}
                maxLength={100}
              />
            </Grid>

            <Grid item xs={6} mt={4}>
              <div className="editAccountlabel">Sequence ID</div>
              <InputSearchField
                type="number"
                label={t("RAL0821")}
                value={plan.planOrder || ""}
                onChange={handleSequenceIdChange}
                error={errors.planOrder}
                helperText={errors.planOrder}
              />
            </Grid>

            {/* <Grid item xs={6} mt={4}>
              <div className="editAccountlabel">
                Free trial period (in days)
              </div>
              <InputSearchField
                type="number"
                label={t("RAL0786")}
                value={plan.trailPeriod ?? ""}
                onChange={handleFreeTrialChange}
                error={errors.trialPeriod}
                helperText={errors.trialPeriod}
              />
            </Grid> */}
          </Box>
        ))}
      </DialogContent>

      <DialogActions className="editAccountbuttons">
        <Box>
          <button className={"billing-prev-button"} onClick={handleClose}>
            {/* {t("RAL0177")} */}
            Cancel
          </button>
          <button className="billing-next-button" onClick={handleSave}>
            {/* {t("RAL0309")} */}
            Save
          </button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default EditAccountInfo;
